import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { ImageDto, OnboardingScreenDto } from "@/api/types";
import { OnboardingScreen } from "@/components/OnboardingScreen/OnboardingScreen";
import { buttonVariants } from "@/design-system/Button";
import { preloadImage } from "@/helpers/image";
import { twResolve } from "@/helpers/tw-resolve";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSlug } from "@/hooks/useSlug";
import { OnboardingFlowRoot } from "@/modules/onboarding-flow/components/OnboardingFlowLayout";
import { OnboardingTopBar } from "@/modules/onboarding-flow/components/OnboardingTopBar";
import { routes } from "@/routes";

export interface OnboardingSlidesProps {
  slides: OnboardingScreenDto[];
}

export function OnboardingSlides({ slides }: OnboardingSlidesProps): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState<OnboardingScreenDto>(slides[0]);
  const resolveImage = useImageResolver();
  const sessionUser = useSessionUser();
  useDocumentTitle(activeSlide.title || activeSlide.subtitle || sessionUser.project.name);

  const handleNextButtonClick = () => {
    const activeSlideIndex = slides.findIndex((slide) => slide.id === activeSlide.id);
    if (activeSlideIndex === slides.length - 1) {
      void navigate(routes.onboardingFlow.codeOfConduct({ slug }));
    } else {
      setActiveSlide(slides[activeSlideIndex + 1]);
    }
  };

  const getImage = useCallback(
    function getImage(image: ImageDto) {
      return resolveImage(image, "intrinsic");
    },
    [resolveImage],
  );

  useEffect(() => {
    const nextImageIndex = slides.findIndex((slide) => slide.id === activeSlide.id) + 1;
    if (nextImageIndex < slides.length) {
      preloadImage(getImage(slides[nextImageIndex].image));
    }
  }, [activeSlide.id, slides, getImage]);

  const withFadeOutBackground = !!(slides[0]?.title || slides[0]?.subtitle);

  return (
    <OnboardingFlowRoot>
      <OnboardingTopBar />
      <main className="z-10 flex w-full flex-1 flex-col overflow-auto p-space-md">
        <article className="mx-auto aspect-9/16 w-full max-w-content-flow" data-testid="onboarding-slide">
          <OnboardingScreen
            imageUrl={getImage(activeSlide.image)}
            title={activeSlide.title}
            subtitle={activeSlide.subtitle}
            withFadeOutBackground={withFadeOutBackground}
            onNext={handleNextButtonClick}
          />
        </article>

        <nav className="mt-space-md flex items-center justify-center gap-space-sm p-space-md">
          {slides.map((slide, index) => (
            <button
              aria-current={activeSlide === slide ? "step" : undefined}
              aria-label={slide.title || slide.subtitle || String(index + 1)}
              className={twResolve(
                buttonVariants({ variant: "ghostSecondary", size: "sm", shape: "circular" }),
                "size-control-sm p-0",
              )}
              key={slide.id}
              onClick={() => setActiveSlide(slide)}
            >
              <span
                className={twResolve(
                  "size-status-dot rounded-full",
                  activeSlide === slide ? "bg-background-brand-secondary" : "bg-background-tertiary",
                )}
              />
            </button>
          ))}
        </nav>
      </main>
    </OnboardingFlowRoot>
  );
}
